import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Topbar from '../components/Topbar';
import DarkCard from '../components/DarkCard';
import DarkButton from '../components/DarkButton';
import ThemeRoot from '../components/ThemeRoot';
import { haptic } from '../lib/haptic';
import { useDarkStore } from '../store/darkStore';

type Tab = 'in-progress' | 'completed';

export default function Plans() {
  const navigate = useNavigate();
  const { stages, answers, explorerName, avatarEmoji, resetPlan } = useDarkStore();

  const doneCount = stages.filter((s) => s.status === 'done').length;
  const totalCount = stages.length;
  const isCompleted = doneCount === totalCount;
  const hasStarted = doneCount > 0 || !!explorerName;

  const [activeTab, setActiveTab] = useState<Tab>(
    isCompleted ? 'completed' : 'in-progress',
  );
  const [showDelete, setShowDelete] = useState(false);
  const [showNewPlanConfirm, setShowNewPlanConfirm] = useState(false);

  // Use the Stage 1 first-answer ("in 2-3 words, what is your idea") as the
  // business name if filled; otherwise a friendly fallback.
  const businessName = useMemo(() => {
    const s1 = (answers.ideaExplosion ?? {}) as Record<string, string>;
    const raw = (s1.s1_q1 ?? '').toString().trim();
    if (raw) return raw;
    return explorerName ? `${explorerName}'s first plan` : 'My First Plan';
  }, [answers, explorerName]);

  // Build a pseudo-plan list. The dark store is single-plan, so if the user
  // hasn't started we show an empty state; otherwise one card lives in the
  // matching tab.
  const planCardVisible =
    hasStarted &&
    ((activeTab === 'in-progress' && !isCompleted) ||
      (activeTab === 'completed' && isCompleted));

  const inProgressCount = hasStarted && !isCompleted ? 1 : 0;
  const completedCount = hasStarted && isCompleted ? 1 : 0;

  const goContinue = () => {
    haptic('tap');
    navigate('/home');
  };

  const goViewPlan = () => {
    haptic('tap');
    navigate('/home/summary');
  };

  const goStart = () => {
    haptic('tap');
    if (hasStarted) {
      // Existing plan in the slot — confirm before wiping it.
      setShowNewPlanConfirm(true);
      return;
    }
    navigate('/home/challenge/1');
  };

  const handleNewPlanConfirm = () => {
    haptic('levelUp');
    resetPlan();
    setShowNewPlanConfirm(false);
    setActiveTab('in-progress');
    navigate('/home/challenge/1');
  };

  const handleDeleteConfirm = () => {
    haptic('wrong');
    resetPlan();
    setShowDelete(false);
    setActiveTab('in-progress');
  };

  return (
    <ThemeRoot>
      <Topbar />

      <main className="mx-auto max-w-3xl px-4 py-8 md:py-12">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="font-marker text-sm uppercase tracking-[0.22em] text-amber/80">
              {explorerName ? `Welcome back, ${explorerName} ${avatarEmoji}` : 'OMore'}
            </div>
            <h1 className="mt-1 font-marker text-4xl text-ink md:text-5xl">
              My Plans
            </h1>
            <p className="mt-1 font-gill text-ink/70">
              {!hasStarted
                ? 'Start your first business adventure.'
                : isCompleted
                  ? 'One plan cleared. Re-read it any time.'
                  : 'Pick up where you left off.'}
            </p>
          </div>
          <DarkButton accent="amber" onClick={goStart}>
            {hasStarted ? '+ New plan' : '+ Start a plan'}
          </DarkButton>
        </div>

        {!hasStarted ? (
          /* Empty state */
          <div className="mt-20 flex flex-col items-center text-center">
            <div className="text-7xl">🚀</div>
            <h2 className="mt-4 font-marker text-2xl text-ink">No plans yet!</h2>
            <p className="mt-2 max-w-sm font-gill text-ink/70">
              Build your first business plan and turn your idea into something
              real.
            </p>
            <DarkButton
              accent="mint"
              className="mt-6"
              hapticKind="levelUp"
              onClick={goStart}
            >
              Start a Plan
            </DarkButton>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="mt-8 flex gap-6 border-b border-white/10">
              {(
                [
                  { key: 'in-progress', label: 'In progress', count: inProgressCount },
                  { key: 'completed', label: 'Completed', count: completedCount },
                ] as { key: Tab; label: string; count: number }[]
              ).map((tab) => {
                const active = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => {
                      haptic('tap');
                      setActiveTab(tab.key);
                    }}
                    className="relative flex items-center gap-2 pb-3 font-marker text-sm uppercase tracking-wider transition-colors"
                    style={{ color: active ? '#F59E0B' : '#9AA0B4' }}
                  >
                    {tab.label}
                    <span
                      className="rounded-full px-2 py-0.5 font-marker text-[11px]"
                      style={
                        active
                          ? { backgroundColor: 'rgba(245,158,11,0.15)', color: '#F59E0B' }
                          : { backgroundColor: 'rgba(255,255,255,0.06)', color: '#9AA0B4' }
                      }
                    >
                      {tab.count}
                    </span>
                    {active && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-amber" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            <div className="mt-6">
              {planCardVisible ? (
                <DarkCard active={isCompleted}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="truncate font-marker text-2xl text-ink">
                          {businessName}
                        </h3>
                        {isCompleted && (
                          <span className="shrink-0 rounded-full border border-mint/40 bg-mint/10 px-3 py-1 font-marker text-xs text-mint">
                            Completed
                          </span>
                        )}
                      </div>
                      <p className="mt-1 font-gill text-sm text-ink/60">
                        by {explorerName || 'Explorer'}
                      </p>

                      {/* Progress pips */}
                      <div className="mt-4 flex items-center gap-3">
                        <div className="flex gap-1.5">
                          {stages.map((s) => (
                            <div
                              key={s.id}
                              className="h-1.5 rounded-full transition-colors"
                              style={{
                                width: 26,
                                backgroundColor:
                                  s.status === 'done'
                                    ? '#10B981'
                                    : s.status === 'active'
                                      ? '#F59E0B'
                                      : 'rgba(255,255,255,0.12)',
                              }}
                            />
                          ))}
                        </div>
                        <span className="font-gill text-xs text-ink/50">
                          {doneCount} / {totalCount} stages
                        </span>
                      </div>

                      {/* Stage chips */}
                      <div className="mt-4 flex flex-wrap gap-1.5">
                        {stages.map((s) => (
                          <span
                            key={s.id}
                            className={`rounded-full border px-2 py-0.5 font-marker text-[11px] ${
                              s.status === 'done'
                                ? 'border-mint/40 bg-mint/10 text-mint'
                                : s.status === 'active'
                                  ? 'border-amber/40 bg-amber/10 text-amber'
                                  : 'border-white/10 bg-white/5 text-ink/40'
                            }`}
                          >
                            {s.emoji} {s.name}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* CTAs */}
                    <div className="flex shrink-0 flex-col items-stretch gap-2">
                      {isCompleted ? (
                        <>
                          <DarkButton accent="mint" onClick={goViewPlan}>
                            View plan
                          </DarkButton>
                          <DarkButton accent="ghost" onClick={goContinue}>
                            Back to map
                          </DarkButton>
                          <button
                            type="button"
                            onClick={() => {
                              haptic('tap');
                              setShowDelete(true);
                            }}
                            className="mt-1 inline-flex items-center justify-center rounded-2xl border border-coral/30 bg-coral/5 px-6 py-2.5 font-marker text-sm text-coral transition-colors hover:border-coral/60 hover:bg-coral/15 active:scale-[0.97]"
                          >
                            🗑 Delete plan
                          </button>
                        </>
                      ) : (
                        <>
                          <DarkButton
                            accent="amber"
                            hapticKind="levelUp"
                            onClick={goContinue}
                          >
                            Continue →
                          </DarkButton>
                          <DarkButton accent="ghost" onClick={goViewPlan}>
                            Sneak peek
                          </DarkButton>
                        </>
                      )}
                    </div>
                  </div>
                </DarkCard>
              ) : (
                <div className="py-16 text-center font-gill text-ink/50">
                  {activeTab === 'in-progress'
                    ? 'No plans in progress. Start a new one!'
                    : 'No completed plans yet. Keep going!'}
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* Delete confirmation */}
      {showDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowDelete(false);
          }}
        >
          <div className="w-full max-w-sm rounded-3xl border border-coral/30 bg-surface p-6 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-coral/40 bg-coral/10 text-xl">
                ⚠️
              </div>
              <div>
                <h3 className="font-marker text-xl text-ink">Delete this plan?</h3>
                <p className="mt-1 font-gill text-sm text-ink/70">
                  This wipes every answer, homework entry, and stage for{' '}
                  <span className="text-ink">{businessName}</span>. It cannot be
                  undone.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <DarkButton accent="ghost" onClick={() => setShowDelete(false)}>
                Cancel
              </DarkButton>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                className="inline-flex items-center justify-center rounded-2xl bg-coral px-5 py-3 font-marker text-base text-white shadow-glow-coral transition-transform hover:brightness-110 active:scale-[0.97]"
              >
                Yes, delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Start a new plan confirmation */}
      {showNewPlanConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowNewPlanConfirm(false);
          }}
        >
          <div className="w-full max-w-sm rounded-3xl border border-amber/30 bg-surface p-6 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-amber/40 bg-amber/10 text-xl">
                ✨
              </div>
              <div>
                <h3 className="font-marker text-xl text-ink">Start a fresh plan?</h3>
                <p className="mt-1 font-gill text-sm text-ink/70">
                  You only have one plan slot. Starting a new plan will replace{' '}
                  <span className="text-ink">{businessName}</span> and clear every
                  answer. This can't be undone.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <DarkButton accent="ghost" onClick={() => setShowNewPlanConfirm(false)}>
                Keep current plan
              </DarkButton>
              <button
                type="button"
                onClick={handleNewPlanConfirm}
                className="inline-flex items-center justify-center rounded-2xl bg-amber px-5 py-3 font-marker text-base text-white shadow-glow-amber transition-transform hover:brightness-110 active:scale-[0.97]"
              >
                Yes, start fresh
              </button>
            </div>
          </div>
        </div>
      )}
    </ThemeRoot>
  );
}
